'use client'

import { cn } from '@/lib/utils'
import { EvaluationDimensionRow } from '@/components/evaluation/evaluation-dimension-row'
import type { EvaluationCriterionResult } from '@/hooks/use-evaluation-analysis'

type EvaluationCriteriaSectionProps = {
  title: string
  criteria: EvaluationCriterionResult[]
  reasoningCollapsible?: boolean
  density?: 'default' | 'compact'
}

export function filterScoredCriteria(criteria: EvaluationCriterionResult[]): EvaluationCriterionResult[] {
  return criteria.filter(
    (criterion) => typeof criterion.score === 'number' && !Number.isNaN(criterion.score),
  )
}

export function EvaluationCriteriaSection({
  title,
  criteria,
  reasoningCollapsible,
  density = 'default',
}: EvaluationCriteriaSectionProps) {
  const scoredCriteria = filterScoredCriteria(criteria)
  if (!scoredCriteria.length) return null

  return (
    <section className={cn(density === 'compact' ? 'space-y-2' : 'space-y-4')}>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</h3>
      {scoredCriteria.map((criterion) => (
        <EvaluationDimensionRow
          key={criterion.key}
          label={criterion.label}
          score={criterion.score as number}
          reasoning={criterion.reasoning}
          reasoningCollapsible={reasoningCollapsible}
          density={density}
        />
      ))}
    </section>
  )
}

export function groupCriteriaResults(criteriaResults: EvaluationCriterionResult[] | undefined) {
  const results = criteriaResults ?? []
  return {
    dimensions: results.filter((c) => c.kind === 'dimension' || !c.kind),
    focusAreas: results.filter((c) => c.kind === 'competency'),
    requirements: results.filter((c) => c.kind === 'requirement'),
  }
}

export function isV2AnalysisPayload(payload: {
  schema_version?: string
  criteria_results?: EvaluationCriterionResult[]
}): boolean {
  if (payload.schema_version === '2') return true
  return Boolean(payload.criteria_results && payload.criteria_results.length > 0)
}
